/**
 * @file Client entry for the static www pages — the island bootloader.
 * @module @labcraft/www/ui/islands/main
 *
 * The only client bundle the site ships (ADR-026). Jobs:
 *
 *   1. Import the site's stylesheet graph (`../styles.ts`) so the
 *      build emits one CSS bundle covering every page (in dev, Vite
 *      injects the same styles through this module).
 *   2. Hydrate every `[data-island]` marker the SSG stamped into the
 *      page: look the island up in {@link REGISTRY}, re-parse the
 *      JSON props, and `hydrateRoot` the identical component over the
 *      server markup.
 *   3. Mount the vanilla enhancers that need no React — the asciinema
 *      replay on `[data-asciinema]` divs (player + CSS load lazily,
 *      only on pages that carry one), and the waitlist-aware label swap
 *      on the static `[data-waitlist-cta]` nav/hero CTAs.
 *
 * The nav/theme/banner chrome is NOT here — it's the inline
 * `CHROME_SCRIPT` in the document (`chrome_scripts.ts`), so it works
 * even while this module is still loading.
 */

import { createElement, type ComponentType } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { BrowserAcquisition } from '@labcraft/core/frontend'
import { KIT_SERVER_COMPONENTS, PAGE_STYLE_SHEETS } from '../styles.ts'
import { NavAuthIsland } from './nav_auth.tsx'
import { SigninCtaIsland } from './signin_cta.tsx'
import { ContactFormIsland } from './contact_form.tsx'
import { IslandRuntime } from './runtime.ts'
import { WAITLIST_COPY } from '../waitlist_copy.ts'

/**
 * Island name → component. Must stay in lockstep with the server's
 * `IslandSlot` call sites — the SSG renders `component` under the same
 * name, so hydrating a different component would mismatch the markup.
 */
const REGISTRY: Record<string, ComponentType<never>> = {
  'signin-cta': SigninCtaIsland as ComponentType<never>,
  'nav-auth': NavAuthIsland as ComponentType<never>,
  'contact-form': ContactFormIsland as ComponentType<never>,
}

/** Boots the page's islands + vanilla enhancers. Static-only. */
class IslandBoot {
  /** Hydrate every island marker and mount the vanilla enhancers. */
  static boot(): void {
    // First-touch acquisition capture. Runs on EVERY page boot so a
    // UTM-tagged entry page (a blog post, a manifesto share) is persisted
    // to localStorage before the visitor browses on to an untagged page and
    // signs in — `Authorize` reads the stored value at sign-in. Write-once
    // (a stored real touch is never overwritten), so repeat boots are
    // cheap; replaces the old per-hop `/#start` `?utm_*` forwarding.
    BrowserAcquisition.captureFirstTouch()

    // Real consumption of the style manifests — keeps the CSS in the
    // bundle (see styles.ts) and catches an empty manifest loud.
    if (PAGE_STYLE_SHEETS.length === 0 || KIT_SERVER_COMPONENTS.length === 0) {
      console.error('www islands: style manifest missing from bundle')
    }
    for (const el of document.querySelectorAll<HTMLElement>('[data-island]')) {
      const name = el.dataset['island'] ?? ''
      const component = REGISTRY[name]
      if (!component) {
        console.error(`www islands: unknown island '${name}'`)
        continue
      }
      const props = JSON.parse(el.dataset['props'] ?? '{}') as never
      hydrateRoot(el, createElement(component, props))
    }

    const casts = document.querySelectorAll<HTMLElement>('[data-asciinema]')
    if (casts.length > 0) {
      void import('./asciinema.ts').then(({ AsciinemaMount }) => {
        for (const el of casts) AsciinemaMount.mount(el)
      })
    }

    // Waitlist-aware CTAs (landing hero + mobile menu) are static
    // anchors, not islands — wrapping them in an island slot would break
    // the flex layouts they sit in (the hero button row, the full-width
    // `menuCtaFull`). So swap only their label here when the live setting
    // says the waitlist is off. Reuses the runtime's promise-cached
    // `/api/config` read, so this adds no fetch over what the islands do.
    const ctas = document.querySelectorAll<HTMLElement>('[data-waitlist-cta]')
    if (ctas.length > 0) {
      void IslandRuntime.config().then(config => {
        const label =
          WAITLIST_COPY[config.waitlistEnabled ? 'on' : 'off'].navCta
        for (const el of ctas) el.textContent = label
      })
    }
  }
}

IslandBoot.boot()
